import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Play } from "@phosphor-icons/react";
import { useContact } from "@/context/ContactContext";
import HeroFlow from "@/components/site/HeroFlow";

const EASE = [0.16, 1, 0.3, 1];

const MaskLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const shapeY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const shapeY2 = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen bg-white text-[#0a0a0a] overflow-hidden flex items-center"
    >
      {/* decorative parallax shapes */}
      <motion.div
        aria-hidden="true"
        style={{ y: shapeY }}
        className="pointer-events-none absolute -right-24 top-24 h-[320px] w-[320px] md:h-[520px] md:w-[520px] rounded-full border-[14px] border-[#f5d949]/50"
      />
      <motion.div
        aria-hidden="true"
        style={{ y: shapeY2 }}
        className="pointer-events-none absolute right-[12%] bottom-[8%] h-40 w-40 md:h-64 md:w-64 rounded-full bg-zinc-100"
      />
      <div className="grain mix-blend-multiply opacity-[0.06]" aria-hidden="true" />

      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-5 md:px-10 pt-32 pb-20">
        <div className="grid md:grid-cols-[1fr_1.5fr] gap-10 lg:gap-14 items-start">
          <div>
            <h1 className="font-display font-black tracking-[-0.03em] leading-[0.95] text-[clamp(1.9rem,4.8vw,4.4rem)]">
              <MaskLine delay={0.15}>Connect wallets.</MaskLine>
              <MaskLine delay={0.32}>Direct Deposit.</MaskLine>
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.6 }}
              className="mt-8 md:mt-10"
            >
              <p className="text-lg md:text-2xl font-medium max-w-2xl text-zinc-600 text-balance">
                Enables any application to securely accept stablecoin deposits
                from external wallets through a seamless embedded flow—eliminating
                copy/paste errors, address poisoning attacks, manual transfer
                steps, and user friction.
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-4">
                <a
                href="https://app.opendeposit.xyz"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-launch-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-black text-white font-bold px-7 py-4 text-base hover:bg-black/85 transition-colors"
              >
                Launch App
                <ArrowUpRight
                  size={20}
                  weight="bold"
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
                <button
                  data-testid="hero-demo-btn"
                  className="group inline-flex items-center gap-2 rounded-full border-2 border-black/25 font-bold px-7 py-4 text-base hover:bg-black/5 transition-colors"
                >
                  <Play size={18} weight="fill" />
                  Watch Demo
                </button>
              </div>
            </motion.div>
          </div>

          {/* animated deposit flow with step cards */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.7 }}
            style={{ opacity: fade }}
            className="hidden md:block"
          >
            <HeroFlow />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
